/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";

const store = {
  get: (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtShort = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}k` : fmt(n);

function calcFI(d) {
  const mi = d.income - d.expenses - d.debtPayment;
  const nw = d.portfolio + d.retirement + d.realEstate - d.debt;
  const fit = (d.monthlyGoal * 12) / (d.withdrawalRate / 100);
  let y = 0, n = nw;
  while (n < fit && y < 80) { n = n * (1 + d.returnRate / 100) + mi * 12; y++; }
  const date = new Date(); date.setFullYear(date.getFullYear() + y);
  const yearsToRetire = Math.max(1, (d.retirementAge || 65) - (d.currentAge || 30));
  const coastTarget = fit / Math.pow(1 + d.returnRate / 100, yearsToRetire);
  return {
    years: y, date, fiTarget: fit, netWorth: nw, monthlyInvest: mi,
    savingsRate: Math.max(0, mi / d.income * 100).toFixed(1),
    pct: Math.min(100, Math.max(0, nw / fit * 100)).toFixed(1),
    fiMonth: date.toLocaleString("default", { month: "long" }), fiYear: date.getFullYear(),
    coastTarget, coastPct: Math.min(100, Math.max(0, nw / coastTarget * 100)).toFixed(1),
    coastReached: nw >= coastTarget,
  };
}

const DEF = { income: 9500, expenses: 4200, debtPayment: 800, portfolio: 145000, retirement: 87000, realEstate: 320000, debt: 210000, monthlyGoal: 5000, withdrawalRate: 4, returnRate: 7, currentAge: 32, retirementAge: 65 };

async function aiCall(messages, system) {
  const body = { messages };
  if (system) body.system = system;
  const res = await fetch("/api/ai", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error("API error " + res.status);
  const d = await res.json();
  return d.text || "";
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@300;400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --slate-950: #0c1220;
  --slate-900: #131f30;
  --slate-800: #1e2d42;
  --slate-700: #2a3d56;
  --slate-600: #3d5068;  
  --slate-400: #7a94ae;
  --slate-300: #a8bdd0;
  --slate-200: #dce6ef;
  --slate-100: #eef3f8;
  --slate-50:  #f5f8fb;
  --white: #ffffff;
  --sage-700: #2d6a4f;
  --sage-600: #3a7d5e;
  --sage-500: #52b788;
  --sage-400: #74c69d;
  --sage-200: #b7e4c7;
  --sage-100: #d8f3dc;
  --sage-50:  #f0faf3;
  --amber: #c8871a;
  --amber-pale: #fdf4e3;
  --red: #c0392b;
  --red-pale: #fdf1f0;
  --coast: #5b4fcf;
  --coast-pale: #f0effe;
  --border: rgba(30, 45, 66, 0.10);
  --border-strong: rgba(30, 45, 66, 0.18);
}

body { font-family: 'Inter', sans-serif; background: var(--white); color: var(--slate-900); -webkit-font-smoothing: antialiased; }
h1,h2,h3 { font-family: 'Playfair Display', serif; }

/* ── NAV ── */
.nav { display:flex; justify-content:space-between; align-items:center; padding: 0 56px; height: 68px; background: var(--white); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 50; }
.logo { font-family: 'Playfair Display', serif; font-size: 21px; font-weight: 500; cursor: pointer; color: var(--slate-900); letter-spacing: -.3px; }
.logo em { font-style: italic; color: var(--sage-600); }
.nav-links { display: flex; gap: 32px; }
.nav-link { font-size: 14px; font-weight: 500; color: var(--slate-400); cursor: pointer; transition: color .15s; }
.nav-link:hover { color: var(--slate-900); }
.nav-right { display: flex; gap: 10px; align-items: center; }
.btn-ghost { padding: 8px 18px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; border: 1px solid var(--border-strong); color: var(--slate-600); border-radius: 6px; transition: all .15s; }
.btn-ghost:hover { border-color: var(--slate-900); color: var(--slate-900); }
.btn-primary { padding: 9px 20px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; background: var(--slate-900); color: var(--white); border: none; border-radius: 6px; transition: all .15s; }
.btn-primary:hover { background: var(--slate-800); }
.btn-sage { background: var(--sage-600); color: var(--white); border: none; padding: 9px 20px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; border-radius: 6px; transition: all .15s; }
.btn-sage:hover { background: var(--sage-700); }

/* ── HERO ── */
.hero { display: grid; grid-template-columns: 1fr 1fr; min-height: calc(100vh - 68px); }
.hero-l { padding: 96px 64px; display: flex; flex-direction: column; justify-content: center; }
.hero-kicker { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: var(--sage-600); margin-bottom: 28px; display: flex; align-items: center; gap: 10px; }
.hero-kicker::before { content: ''; width: 28px; height: 1px; background: var(--sage-500); }
.hero-h1 { font-family: 'Playfair Display', serif; font-size: clamp(44px, 4vw, 66px); line-height: 1.05; letter-spacing: -.5px; color: var(--slate-950); margin-bottom: 24px; font-weight: 500; }
.hero-h1 em { font-style: italic; color: var(--sage-600); }
.hero-body { font-size: 17px; line-height: 1.75; color: var(--slate-600); max-width: 460px; margin-bottom: 44px; font-weight: 300; }
.hero-body strong { font-weight: 600; color: var(--slate-800); }
.hero-btns { display: flex; gap: 12px; margin-bottom: 52px; }
.btn-hero { padding: 14px 32px; font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; border-radius: 6px; transition: all .18s; }
.btn-hero.primary { background: var(--slate-900); color: var(--white); border: none; box-shadow: 0 4px 14px rgba(12,18,32,.15); }
.btn-hero.primary:hover { background: var(--slate-800); box-shadow: 0 6px 20px rgba(12,18,32,.2); }
.btn-hero.ghost { background: transparent; color: var(--slate-600); border: 1px solid var(--border-strong); }
.btn-hero.ghost:hover { color: var(--slate-900); border-color: var(--slate-400); }
.hero-proof { display: flex; gap: 36px; }
.proof-stat { }
.proof-num { font-family: 'Playfair Display', serif; font-size: 28px; color: var(--slate-900); font-weight: 500; }
.proof-lbl { font-size: 12px; color: var(--slate-400); margin-top: 2px; font-weight: 400; }
.hero-r { background: var(--slate-950); display: flex; align-items: center; justify-content: center; padding: 64px; position: relative; overflow: hidden; }
.hero-r::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 20% 60%, rgba(82,183,136,.08) 0%, transparent 55%), radial-gradient(ellipse at 85% 15%, rgba(91,79,207,.07) 0%, transparent 50%); }
.mock-wrap { width: 100%; max-width: 380px; position: relative; z-index: 1; }
.mock-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,.3); margin-bottom: 8px; }
.mock-date-big { font-family: 'Playfair Display', serif; font-size: 52px; color: var(--sage-400); font-weight: 400; line-height: 1; letter-spacing: -1px; margin-bottom: 6px; }
.mock-date-sub { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--sage-400); opacity: .6; margin-bottom: 36px; }
.mock-prog-label { display: flex; justify-content: space-between; font-family: 'JetBrains Mono', monospace; font-size: 9px; color: rgba(255,255,255,.25); margin-bottom: 6px; }
.mock-prog-track { height: 2px; background: rgba(255,255,255,.07); border-radius: 1px; margin-bottom: 28px; }
.mock-prog-fill { height: 100%; background: var(--sage-500); border-radius: 1px; opacity: .75; }
.mock-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-top: 1px solid rgba(255,255,255,.06); }
.mock-stat { padding: 18px 0; border-bottom: 1px solid rgba(255,255,255,.06); }
.mock-stat:nth-child(odd) { padding-right: 24px; border-right: 1px solid rgba(255,255,255,.06); }
.mock-stat:nth-child(even) { padding-left: 24px; }
.mock-stat-v { font-family: 'Playfair Display', serif; font-size: 22px; color: var(--white); font-weight: 400; }
.mock-stat-l { font-family: 'JetBrains Mono', monospace; font-size: 9px; color: rgba(255,255,255,.28); margin-top: 3px; }

/* ── WHY SECTION ── */
.why-section { padding: 112px 56px; background: var(--slate-50); }
.section-kicker { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--sage-600); margin-bottom: 18px; display: flex; align-items: center; gap: 10px; }
.section-kicker::before { content: ''; width: 20px; height: 1px; background: var(--sage-500); }
.section-h2 { font-family: 'Playfair Display', serif; font-size: clamp(34px, 3vw, 48px); line-height: 1.1; color: var(--slate-950); font-weight: 500; letter-spacing: -.3px; max-width: 560px; margin-bottom: 64px; }
.why-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 48px 64px; }
.why-item { }
.why-num { font-family: 'Playfair Display', serif; font-size: 44px; color: var(--sage-200); font-weight: 400; line-height: 1; margin-bottom: 14px; }
.why-title { font-size: 15px; font-weight: 600; color: var(--slate-900); margin-bottom: 8px; }
.why-desc { font-size: 14px; line-height: 1.75; color: var(--slate-600); font-weight: 300; }

/* ── FEATURES ── */
.features-section { padding: 112px 56px; background: var(--white); }
.features-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin-top: 64px; border-top: 1px solid var(--border); border-left: 1px solid var(--border); }
.feature-item { padding: 40px 36px; border-right: 1px solid var(--border); border-bottom: 1px solid var(--border); transition: background .2s; }
.feature-item:hover { background: var(--sage-50); }
.feature-tag { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--sage-600); margin-bottom: 12px; }
.feature-title { font-size: 16px; font-weight: 600; color: var(--slate-900); margin-bottom: 8px; }
.feature-desc { font-size: 14px; line-height: 1.7; color: var(--slate-500); font-weight: 300; }

/* ── PRICING ── */
.pricing-section { padding: 112px 56px; background: var(--slate-50); }
.pricing-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; max-width: 860px; margin: 64px auto 0; border: 1px solid var(--border); }
.price-col { padding: 40px 32px; border-right: 1px solid var(--border); position: relative; }
.price-col:last-child { border-right: none; }
.price-col.featured { background: var(--slate-950); color: var(--white); }
.price-badge { position: absolute; top: -1px; left: 50%; transform: translateX(-50%); background: var(--sage-600); color: var(--white); font-family: 'JetBrains Mono', monospace; font-size: 9px; padding: 4px 14px; white-space: nowrap; }
.price-tier { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--sage-600); margin-bottom: 16px; }
.price-col.featured .price-tier { color: var(--sage-400); }
.price-amount { font-family: 'Playfair Display', serif; font-size: 54px; letter-spacing: -2px; color: var(--slate-950); font-weight: 400; margin-bottom: 4px; }
.price-col.featured .price-amount { color: var(--white); }
.price-period { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--slate-400); margin-bottom: 28px; }
.price-col.featured .price-period { color: rgba(255,255,255,.35); }
.price-list { list-style: none; margin-bottom: 32px; }
.price-list li { padding: 8px 0; font-size: 13px; border-bottom: 1px solid var(--border); color: var(--slate-600); display: flex; gap: 10px; align-items: flex-start; }
.price-col.featured .price-list li { border-color: rgba(255,255,255,.08); color: rgba(255,255,255,.65); }
.price-list li::before { content: '✓'; color: var(--sage-600); font-weight: 700; font-size: 11px; flex-shrink: 0; margin-top: 1px; }
.price-col.featured .price-list li::before { color: var(--sage-400); }
.price-btn { width: 100%; padding: 12px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid var(--border-strong); background: transparent; color: var(--slate-800); border-radius: 4px; transition: all .15s; }
.price-btn:hover { background: var(--slate-900); color: var(--white); border-color: var(--slate-900); }
.price-btn.featured { background: var(--sage-600); border-color: var(--sage-600); color: var(--white); }
.price-btn.featured:hover { background: var(--sage-500); }

/* ── MODALS ── */
.overlay { position: fixed; inset: 0; background: rgba(12,18,32,.55); backdrop-filter: blur(6px); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; }
.modal { background: var(--white); width: 100%; max-width: 420px; position: relative; border-radius: 8px; box-shadow: 0 24px 64px rgba(12,18,32,.18); overflow: hidden; }
.modal-header { padding: 36px 40px 0; }
.modal-body { padding: 24px 40px 40px; }
.modal-close { position: absolute; top: 16px; right: 18px; background: none; border: none; font-size: 20px; cursor: pointer; color: var(--slate-400); line-height: 1; }
.modal-title { font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 500; color: var(--slate-950); margin-bottom: 4px; }
.modal-sub { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--slate-400); }
.field { margin-bottom: 16px; }
.field-label { display: block; font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1px; color: var(--slate-400); margin-bottom: 6px; text-transform: uppercase; }
.field-input { width: 100%; padding: 11px 14px; font-family: 'Inter', sans-serif; font-size: 14px; background: var(--slate-50); border: 1px solid var(--border-strong); color: var(--slate-950); outline: none; border-radius: 4px; transition: border-color .15s; }
.field-input:focus { border-color: var(--sage-500); background: var(--white); }
.err-msg { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--red); margin-bottom: 12px; }
.btn-full { width: 100%; padding: 13px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; background: var(--slate-950); color: var(--white); border: none; border-radius: 4px; transition: all .15s; }
.btn-full:hover:not(:disabled) { background: var(--slate-800); }
.btn-full:disabled { opacity: .45; cursor: not-allowed; }
.modal-toggle { text-align: center; margin-top: 16px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--slate-400); }
.modal-toggle button { background: none; border: none; color: var(--sage-600); cursor: pointer; font-size: 11px; text-decoration: underline; }
.divider-row { display: flex; align-items: center; gap: 14px; margin: 20px 0; }
.divider-line { flex: 1; height: 1px; background: var(--border); }
.divider-text { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); }
.google-btn { width: 100%; padding: 11px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 500; cursor: pointer; background: var(--white); color: var(--slate-800); border: 1px solid var(--border-strong); display: flex; align-items: center; justify-content: center; gap: 10px; border-radius: 4px; transition: all .15s; }
.google-btn:hover { border-color: var(--slate-400); }

/* ── STRIPE MODAL ── */
.stripe-wrap { background: var(--white); width: 100%; max-width: 460px; border-radius: 8px; overflow: hidden; box-shadow: 0 24px 64px rgba(12,18,32,.2); }
.stripe-top { background: var(--slate-950); padding: 32px; }
.stripe-top-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.stripe-toggle { display: flex; background: rgba(255,255,255,.07); border-radius: 4px; padding: 3px; margin-bottom: 18px; }
.stripe-toggle button { flex: 1; padding: 8px; font-family: 'JetBrains Mono', monospace; font-size: 10px; cursor: pointer; background: transparent; border: none; color: rgba(255,255,255,.35); border-radius: 3px; transition: all .15s; }
.stripe-toggle button.on { background: rgba(255,255,255,.12); color: var(--white); }
.stripe-price-big { font-family: 'Playfair Display', serif; font-size: 42px; letter-spacing: -1px; color: var(--sage-400); font-weight: 400; }
.stripe-price-sub { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: rgba(255,255,255,.3); margin-top: 4px; }
.stripe-btm { padding: 28px 32px; }
.stripe-inp { width: 100%; padding: 10px 13px; font-family: 'Inter', sans-serif; font-size: 13px; background: var(--slate-50); border: 1px solid var(--border-strong); color: var(--slate-950); outline: none; margin-bottom: 12px; border-radius: 4px; transition: border-color .15s; }
.stripe-inp:focus { border-color: var(--sage-500); }
.card-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.stripe-submit { width: 100%; padding: 13px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; background: var(--sage-600); color: var(--white); border: none; border-radius: 4px; transition: all .15s; margin-top: 4px; }
.stripe-submit:hover:not(:disabled) { background: var(--sage-700); }
.stripe-submit:disabled { opacity: .45; cursor: not-allowed; }
.stripe-foot { font-family: 'JetBrains Mono', monospace; font-size: 9px; color: var(--slate-400); text-align: center; margin-top: 12px; }
.save-pill { background: var(--sage-600); color: var(--white); font-size: 9px; padding: 2px 8px; border-radius: 10px; margin-left: 6px; }

/* ── ONBOARDING ── */
.onboard { min-height: 100vh; display: flex; flex-direction: column; background: var(--slate-950); }
.onboard-nav { padding: 20px 48px; border-bottom: 1px solid rgba(255,255,255,.06); display: flex; justify-content: space-between; align-items: center; }
.onboard-pips { display: flex; gap: 5px; }
.onboard-pip { width: 28px; height: 2px; background: rgba(255,255,255,.1); border-radius: 1px; transition: background .3s; }
.onboard-pip.done { background: var(--sage-500); }
.onboard-main { flex: 1; display: flex; flex-direction: column; max-width: 680px; margin: 0 auto; padding: 56px 48px; width: 100%; }
.onboard-thread { flex: 1; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; max-height: calc(100vh - 280px); margin-bottom: 28px; }
.onboard-msg { display: flex; gap: 14px; align-items: flex-start; }
.onboard-msg.user { flex-direction: row-reverse; }
.onboard-av { width: 34px; height: 34px; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 500; }
.onboard-av.ai { background: rgba(82,183,136,.12); color: var(--sage-400); border: 1px solid rgba(82,183,136,.2); }
.onboard-av.me { background: rgba(255,255,255,.06); color: rgba(255,255,255,.45); }
.onboard-bubble { padding: 14px 18px; font-size: 14px; line-height: 1.7; max-width: 86%; border-radius: 6px; }
.onboard-msg.ai .onboard-bubble { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.07); color: rgba(255,255,255,.8); }
.onboard-msg.user .onboard-bubble { background: var(--sage-700); color: var(--white); font-weight: 500; }
.onboard-input-row { display: flex; gap: 10px; }
.onboard-inp { flex: 1; background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.09); color: var(--white); font-family: 'Inter', sans-serif; font-size: 14px; padding: 13px 18px; outline: none; border-radius: 6px; transition: border-color .15s; }
.onboard-inp::placeholder { color: rgba(255,255,255,.2); }
.onboard-inp:focus { border-color: var(--sage-500); }
.onboard-send { background: var(--sage-600); color: var(--white); border: none; padding: 13px 24px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; border-radius: 6px; transition: all .15s; }
.onboard-send:hover:not(:disabled) { background: var(--sage-500); }
.onboard-send:disabled { opacity: .35; cursor: not-allowed; }
.onboard-skip { background: none; border: none; color: rgba(255,255,255,.22); font-family: 'JetBrains Mono', monospace; font-size: 10px; cursor: pointer; text-decoration: underline; padding: 0; }
.onboard-skip:hover { color: rgba(255,255,255,.45); }
.dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: var(--sage-500); animation: pulse-dot 1.2s ease-in-out infinite; }
.dot:nth-child(2) { animation-delay: .2s; }
.dot:nth-child(3) { animation-delay: .4s; }
@keyframes pulse-dot { 0%,100%{opacity:.2;}50%{opacity:1;} }

/* ── APP SHELL ── */
.shell { display: grid; grid-template-columns: 220px 1fr; min-height: 100vh; }
.sidebar { background: var(--slate-950); color: var(--white); padding: 28px 18px; display: flex; flex-direction: column; position: sticky; top: 0; height: 100vh; overflow-y: auto; }
.s-brand { font-family: 'Playfair Display', serif; font-size: 19px; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid rgba(255,255,255,.06); cursor: pointer; }
.s-brand em { font-style: italic; color: var(--sage-400); }
.s-user { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: rgba(255,255,255,.04); border-radius: 6px; margin-bottom: 10px; }
.s-av { width: 32px; height: 32px; background: var(--sage-700); color: var(--white); display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px; border-radius: 5px; flex-shrink: 0; }
.s-name { font-size: 13px; font-weight: 600; }
.s-plan { font-family: 'JetBrains Mono', monospace; font-size: 8px; color: var(--sage-400); margin-top: 1px; }
.s-nav { margin: 4px 0; }
.s-item { padding: 9px 12px; font-size: 13px; font-weight: 500; cursor: pointer; color: rgba(255,255,255,.38); border-radius: 5px; margin-bottom: 1px; transition: all .15s; display: flex; align-items: center; gap: 9px; }
.s-item:hover { background: rgba(255,255,255,.05); color: rgba(255,255,255,.7); }
.s-item.active { background: rgba(82,183,136,.1); color: var(--sage-400); }
.s-icon { font-size: 14px; opacity: .7; }
.s-sep { height: 1px; background: rgba(255,255,255,.06); margin: 10px 0; }
.s-fi-widget { margin-top: auto; padding: 18px; background: rgba(82,183,136,.07); border: 1px solid rgba(82,183,136,.15); border-radius: 6px; }
.s-fi-lbl { font-family: 'JetBrains Mono', monospace; font-size: 8px; letter-spacing: 2px; color: rgba(82,183,136,.7); margin-bottom: 6px; text-transform: uppercase; }
.s-fi-date { font-family: 'Playfair Display', serif; font-size: 24px; color: var(--white); font-weight: 400; }
.s-fi-sub { font-family: 'JetBrains Mono', monospace; font-size: 9px; color: var(--sage-400); opacity: .65; margin-top: 4px; }

/* ── MAIN CONTENT ── */
.main { background: var(--white); overflow-y: auto; }
.main-inner { padding: 48px 56px; max-width: 1080px; }

/* ── PAGE HEADER ── */
.pg-header { margin-bottom: 48px; display: flex; justify-content: space-between; align-items: flex-start; }
.pg-title { font-family: 'Playfair Display', serif; font-size: 36px; font-weight: 500; color: var(--slate-950); letter-spacing: -.3px; }
.pg-sub { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-top: 6px; }
.live-badge { display: flex; align-items: center; gap: 6px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); }
.live-dot { width: 6px; height: 6px; background: var(--sage-500); border-radius: 50%; animation: live-pulse 2s ease-in-out infinite; }
@keyframes live-pulse { 0%,100%{opacity:1;transform:scale(1);}50%{opacity:.5;transform:scale(.8);} }

/* ── FI HERO SECTION ── */
.fi-hero { padding: 52px 0; border-bottom: 1px solid var(--border); margin-bottom: 52px; display: grid; grid-template-columns: auto 1fr auto; gap: 64px; align-items: center; }
.fi-date-col { }
.fi-date-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 2px; color: var(--slate-400); text-transform: uppercase; margin-bottom: 8px; }
.fi-date-big { font-family: 'Playfair Display', serif; font-size: 72px; line-height: 1; letter-spacing: -3px; color: var(--slate-950); font-weight: 500; }
.fi-date-big em { font-style: italic; color: var(--sage-600); }
.fi-date-sub { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--slate-400); margin-top: 8px; }
.fi-prog-col { }
.fi-prog-label-row { display: flex; justify-content: space-between; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-bottom: 10px; }
.fi-prog-track { height: 3px; background: var(--slate-100); border-radius: 2px; margin-bottom: 10px; }
.fi-prog-fill { height: 100%; background: var(--sage-500); border-radius: 2px; transition: width .8s ease; }
.fi-prog-amounts { display: flex; justify-content: space-between; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); }
.fi-right-col { text-align: right; }
.fi-right-num { font-family: 'Playfair Display', serif; font-size: 36px; color: var(--sage-600); font-weight: 400; }
.fi-right-lbl { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-top: 4px; }

/* ── STATS ROW ── */
.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); margin-bottom: 52px; }
.stat-item { padding: 28px 0; border-right: 1px solid var(--border); cursor: pointer; transition: background .15s; position: relative; }
.stat-item:last-child { border-right: none; }
.stat-item:hover { background: var(--slate-50); }
.stat-item:nth-child(n+2) { padding-left: 28px; }
.stat-lbl { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--slate-400); margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
.stat-val { font-family: 'Playfair Display', serif; font-size: 30px; letter-spacing: -.5px; color: var(--slate-950); font-weight: 400; }
.stat-val.pos { color: var(--sage-600); }
.stat-val.neg { color: var(--red); }
.stat-sub { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-top: 5px; }
.stat-explain { position: absolute; top: 10px; right: 10px; background: none; border: 1px solid var(--border-strong); color: var(--slate-400); font-family: 'JetBrains Mono', monospace; font-size: 9px; padding: 3px 7px; cursor: pointer; border-radius: 3px; opacity: 0; transition: opacity .15s; }
.stat-item:hover .stat-explain { opacity: 1; }

/* ── COAST FIRE SECTION ── */
.coast-section { padding: 40px 0; border-bottom: 1px solid var(--border); margin-bottom: 52px; display: grid; grid-template-columns: 1fr 2fr 1fr; gap: 48px; align-items: center; }
.coast-lbl { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: var(--coast); margin-bottom: 8px; }
.coast-val-big { font-family: 'Playfair Display', serif; font-size: 38px; letter-spacing: -1px; color: var(--slate-950); font-weight: 400; }
.coast-desc { font-size: 13px; color: var(--slate-500); margin-top: 6px; line-height: 1.6; font-weight: 300; }
.coast-prog-lbl { display: flex; justify-content: space-between; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-bottom: 8px; }
.coast-track { height: 3px; background: var(--slate-100); border-radius: 2px; }
.coast-fill { height: 100%; background: var(--coast); border-radius: 2px; opacity: .7; transition: width .8s ease; }
.coast-reached { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--sage-600); background: var(--sage-50); border: 1px solid var(--sage-200); padding: 8px 16px; border-radius: 20px; display: inline-block; }

/* ── SECTION HEADERS ── */
.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 1px solid var(--border); }
.section-title { font-size: 14px; font-weight: 600; color: var(--slate-800); display: flex; align-items: center; gap: 10px; }
.ai-pill { background: var(--sage-50); color: var(--sage-700); font-family: 'JetBrains Mono', monospace; font-size: 9px; padding: 3px 9px; border-radius: 10px; border: 1px solid var(--sage-200); }
.section-action { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); cursor: pointer; background: none; border: 1px solid var(--border-strong); padding: 5px 12px; border-radius: 4px; transition: all .15s; display: flex; align-items: center; gap: 5px; }
.section-action:hover:not(:disabled) { color: var(--slate-800); border-color: var(--slate-400); }
.section-action:disabled { opacity: .4; cursor: not-allowed; }

/* ── AI INSIGHTS ── */
.insights-section { margin-bottom: 52px; }
.insights-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 0; border-top: 1px solid var(--border); border-left: 1px solid var(--border); }
.insight-item { padding: 28px 24px; border-right: 1px solid var(--border); border-bottom: 1px solid var(--border); }
.insight-type { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 10px; }
.insight-headline { font-size: 14px; font-weight: 600; color: var(--slate-900); margin-bottom: 8px; line-height: 1.4; }
.insight-text { font-size: 13px; line-height: 1.7; color: var(--slate-500); font-weight: 300; }
.insight-impact { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-top: 14px; }
.insight-impact-num { font-family: 'Playfair Display', serif; font-size: 22px; margin-top: 3px; font-weight: 400; }
.ai-loading-row { display: flex; align-items: center; gap: 10px; color: var(--slate-400); font-family: 'JetBrains Mono', monospace; font-size: 11px; padding: 32px 0; }
.ai-err { background: var(--red-pale); border: 1px solid rgba(192,57,43,.12); padding: 12px 16px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--red); border-radius: 4px; }

/* ── SCENARIO BUILDER ── */
.scenario-section { margin-bottom: 52px; }
.scenario-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 18px; }
.s-chip { background: var(--slate-50); border: 1px solid var(--border-strong); color: var(--slate-600); font-family: 'JetBrains Mono', monospace; font-size: 10px; padding: 6px 12px; cursor: pointer; border-radius: 20px; transition: all .15s; }
.s-chip:hover { border-color: var(--sage-500); color: var(--sage-700); background: var(--sage-50); }
.scenario-input-row { display: flex; gap: 10px; margin-bottom: 20px; }
.scenario-inp { flex: 1; background: var(--slate-50); border: 1px solid var(--border-strong); color: var(--slate-900); font-family: 'Inter', sans-serif; font-size: 14px; padding: 12px 16px; outline: none; border-radius: 6px; transition: border-color .15s; }
.scenario-inp::placeholder { color: var(--slate-400); }
.scenario-inp:focus { border-color: var(--sage-500); background: var(--white); }
.scenario-run { background: var(--slate-900); color: var(--white); border: none; padding: 12px 22px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; border-radius: 6px; white-space: nowrap; transition: all .15s; }
.scenario-run:hover:not(:disabled) { background: var(--slate-800); }
.scenario-run:disabled { opacity: .4; cursor: not-allowed; }
.scenario-result { padding: 28px 0; border-top: 1px solid var(--border); }
.scenario-q { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-bottom: 10px; }
.scenario-summary { font-size: 15px; line-height: 1.75; color: var(--slate-700); font-weight: 300; margin-bottom: 16px; }
.scenario-delta { font-family: 'Playfair Display', serif; font-size: 36px; letter-spacing: -1px; font-weight: 400; }
.scenario-assumption { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); margin-top: 8px; }

/* ── INPUTS PAGE ── */
.inputs-section { margin-bottom: 52px; }
.inputs-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-top: 1px solid var(--border); }
.input-group { padding: 32px 0; border-bottom: 1px solid var(--border); }
.input-group:nth-child(odd) { padding-right: 48px; border-right: 1px solid var(--border); }
.input-group:nth-child(even) { padding-left: 48px; }
.input-group-title { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--slate-400); margin-bottom: 20px; }
.inp-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid var(--border); }
.inp-row:last-child { border-bottom: none; }
.inp-lbl { font-size: 13px; color: var(--slate-600); font-weight: 400; }
.inp-field { font-family: 'JetBrains Mono', monospace; font-size: 13px; background: var(--slate-50); border: 1px solid var(--border); padding: 6px 10px; width: 120px; text-align: right; color: var(--slate-900); outline: none; border-radius: 3px; transition: border-color .15s; }
.inp-field:focus { border-color: var(--sage-500); }

/* ── CHART ── */
.chart-section { margin-bottom: 52px; }
.chart-bars { height: 140px; display: flex; align-items: flex-end; gap: 2px; }
.c-bar { flex: 1; min-height: 2px; transition: height .4s ease; border-radius: 1px 1px 0 0; }
.c-bar.actual { background: var(--sage-500); opacity: .6; }
.c-bar.projected { background: var(--slate-200); }
.chart-leg { display: flex; gap: 18px; margin-top: 12px; }
.chart-leg-item { display: flex; align-items: center; gap: 6px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--slate-400); }
.chart-swatch { width: 10px; height: 10px; border-radius: 2px; }

/* ── AI CHAT ── */
.chat-section { margin-bottom: 52px; }
.chat-thread { max-height: 320px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; margin-bottom: 18px; padding-bottom: 4px; }
.chat-msg { display: flex; gap: 12px; align-items: flex-start; }
.chat-msg.user { flex-direction: row-reverse; }
.chat-av { width: 28px; height: 28px; border-radius: 5px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-family: 'JetBrains Mono', monospace; font-size: 9px; font-weight: 500; }
.chat-av.ai { background: var(--sage-50); color: var(--sage-700); border: 1px solid var(--sage-200); }
.chat-av.me { background: var(--slate-900); color: rgba(255,255,255,.7); }
.chat-bubble { padding: 12px 16px; font-size: 14px; line-height: 1.65; max-width: 78%; border-radius: 6px; }
.chat-msg.ai .chat-bubble { background: var(--slate-50); color: var(--slate-700); border: 1px solid var(--border); }
.chat-msg.user .chat-bubble { background: var(--slate-900); color: var(--white); font-weight: 400; }
.chat-input-row { display: flex; gap: 10px; }
.chat-inp { flex: 1; background: var(--slate-50); border: 1px solid var(--border-strong); color: var(--slate-900); font-family: 'Inter', sans-serif; font-size: 14px; padding: 12px 16px; outline: none; border-radius: 6px; transition: border-color .15s; }
.chat-inp::placeholder { color: var(--slate-400); }
.chat-inp:focus { border-color: var(--sage-500); background: var(--white); }
.chat-send { background: var(--sage-600); color: var(--white); border: none; padding: 12px 22px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; border-radius: 6px; transition: all .15s; }
.chat-send:hover:not(:disabled) { background: var(--sage-700); }
.chat-send:disabled { opacity: .4; cursor: not-allowed; }
.chat-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
.chat-chip { background: var(--slate-50); border: 1px solid var(--border-strong); color: var(--slate-600); font-family: 'JetBrains Mono', monospace; font-size: 10px; padding: 6px 12px; cursor: pointer; border-radius: 20px; transition: all .15s; }
.chat-chip:hover { border-color: var(--sage-500); color: var(--sage-700); }

/* ── PAYWALL ── */
.paywall { padding: 56px 0; text-align: center; }
.paywall-title { font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 400; color: var(--slate-950); margin-bottom: 10px; }
.paywall-sub { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--slate-400); margin-bottom: 28px; line-height: 1.7; max-width: 360px; margin-left: auto; margin-right: auto; }
.paywall-feats { display: flex; justify-content: center; gap: 32px; margin-bottom: 32px; flex-wrap: wrap; }
.paywall-feat { font-size: 13px; color: var(--slate-600); display: flex; align-items: center; gap: 6px; }
.paywall-feat::before { content: '✓'; color: var(--sage-600); font-weight: 700; }

/* ── EXPLAIN POPUP ── */
.explain-pop { position: fixed; z-index: 200; background: var(--slate-950); color: var(--white); padding: 20px 22px; max-width: 300px; border-top: 2px solid var(--sage-500); font-size: 13px; line-height: 1.7; box-shadow: 0 20px 60px rgba(12,18,32,.25); border-radius: 0 0 6px 6px; }
.explain-pop-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: var(--sage-400); margin-bottom: 10px; }
.explain-pop-close { display: block; margin-top: 12px; color: var(--sage-400); cursor: pointer; font-family: 'JetBrains Mono', monospace; font-size: 10px; text-decoration: underline; background: none; border: none; }

/* ── MISC ── */
.toast { position: fixed; bottom: 24px; right: 24px; background: var(--slate-950); color: var(--white); padding: 14px 20px; font-family: 'Inter', sans-serif; font-size: 13px; z-index: 300; border-radius: 6px; box-shadow: 0 16px 48px rgba(12,18,32,.2); animation: toast-in .2s ease; display: flex; align-items: center; gap: 10px; }
.toast-check { width: 20px; height: 20px; background: var(--sage-600); color: var(--white); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; flex-shrink: 0; }
@keyframes toast-in { from{transform:translateY(16px);opacity:0;}to{transform:translateY(0);opacity:1;} }
.about-section { padding: 112px 56px; background: var(--slate-950); color: var(--white); }
.about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 96px; align-items: center; margin-top: 64px; }
.about-h2 { font-family: 'Playfair Display', serif; font-size: clamp(34px,3vw,48px); font-weight: 500; line-height: 1.1; letter-spacing: -.3px; margin-bottom: 24px; }
.about-h2 em { font-style: italic; color: var(--sage-400); }
.about-body { font-size: 16px; line-height: 1.85; color: rgba(255,255,255,.55); font-weight: 300; margin-bottom: 16px; }
.about-body strong { color: rgba(255,255,255,.85); font-weight: 500; }
.about-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid rgba(255,255,255,.08); margin-top: 8px; }
.about-stat { padding: 28px 24px; border-right: 1px solid rgba(255,255,255,.08); border-bottom: 1px solid rgba(255,255,255,.08); }
.about-stat:nth-child(even) { border-right: none; }
.about-stat:nth-child(n+3) { border-bottom: none; }
.about-stat-num { font-family: 'Playfair Display', serif; font-size: 36px; color: var(--sage-400); font-weight: 400; letter-spacing: -1px; }
.about-stat-lbl { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: rgba(255,255,255,.3); margin-top: 4px; text-transform: uppercase; letter-spacing: 1px; }
.upgrade-bar { background: var(--sage-50); border: 1px solid var(--sage-200); padding: 10px 16px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; border-radius: 4px; }
.upgrade-bar-text { font-size: 13px; color: var(--sage-800); font-weight: 500; }
.upgrade-bar-sub { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--sage-600); margin-top: 2px; }
`;

// ─── AUTH MODAL ───────────────────────────────────────────────────────────────
function AuthModal({ mode, onClose, onSuccess }) {
  const [tab, setTab] = useState(mode);
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [name, setName] = useState("");
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const handle = async () => {
    if (!email || !pw) return setError("Please fill in all fields.");
    if (tab === "signup" && !name) return setError("Please enter your name.");
    if (!/\S+@\S+\.\S+/.test(email)) return setError("Invalid email address.");
    if (pw.length < 6) return setError("Password must be 6+ characters.");
    setLoading(true); setError("");
    await new Promise(r => setTimeout(r, 700));
    if (tab === "signup") {
      if (store.get(`user:${email}`)) { setError("Account exists. Sign in instead."); setLoading(false); return; }
      const user = { email, name, plan: "free", createdAt: new Date().toISOString() };
      store.set(`user:${email}`, user); onSuccess(user);
    } else {
      const user = store.get(`user:${email}`);
      if (!user) { setError("No account found. Sign up first."); setLoading(false); return; }
      onSuccess(user);
    }
    setLoading(false);
  };
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="modal-header">
          <div className="modal-title">{tab === "signin" ? "Welcome back." : "Get started."}</div>
          <div className="modal-sub" style={{ marginTop: 4 }}>{tab === "signin" ? "Sign in to your SovereignFI account." : "Your financial independence date awaits."}</div>
        </div>
        <div className="modal-body">
          <button className="google-btn">
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <div className="divider-row"><div className="divider-line"/><span className="divider-text">or</span><div className="divider-line"/></div>
          {tab === "signup" && <div className="field"><label className="field-label">Full Name</label><input className="field-input" placeholder="Alex Johnson" value={name} onChange={e => setName(e.target.value)}/></div>}
          <div className="field"><label className="field-label">Email</label><input className="field-input" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handle()}/></div>
          <div className="field"><label className="field-label">Password</label><input className="field-input" type="password" placeholder="6+ characters" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && handle()}/></div>
          {error && <div className="err-msg">{error}</div>}
          <button className="btn-full" onClick={handle} disabled={loading}>{loading ? "Please wait…" : tab === "signin" ? "Sign In" : "Create Account"}</button>
          <div className="modal-toggle">{tab === "signin" ? <><span>No account? </span><button onClick={() => { setTab("signup"); setError(""); }}>Sign up free</button></> : <><span>Have an account? </span><button onClick={() => { setTab("signin"); setError(""); }}>Sign in</button></>}</div>
        </div>
      </div>
    </div>
  );
}

// ─── STRIPE MODAL ─────────────────────────────────────────────────────────────
function StripeModal({ user, onClose, onSuccess }) {
  const [cycle, setCycle] = useState("annual");
  const [card, setCard] = useState(""); const [exp, setExp] = useState(""); const [cvc, setCvc] = useState(""); const [name, setName] = useState(user?.name || "");
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const price = cycle === "annual" ? "$99/year" : "$12/month";
  const fmtCard = v => v.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim();
  const fmtExp = v => { const d = v.replace(/\D/g,"").slice(0,4); return d.length > 2 ? d.slice(0,2) + "/" + d.slice(2) : d; };
  const handle = async () => {
    if (!name || !card || !exp || !cvc) return setError("Please fill in all fields.");
    if (card.replace(/\s/g,"").length < 16) return setError("Enter a valid card number.");
    setLoading(true); setError("");
    await new Promise(r => setTimeout(r, 1400));
    const updated = { ...user, plan: "sovereign", planCycle: cycle, subscribedAt: new Date().toISOString() };
    store.set(`user:${updated.email}`, updated); onSuccess(updated);
  };
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="stripe-wrap">
        <div className="stripe-top">
          <div className="stripe-top-row">
            <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 19, color: "white" }}>Sovereign<em style={{ fontStyle: "italic", color: "var(--sage-400)" }}>FI</em></span>
            <div style={{ display: "flex", gap: 5 }}>{["VISA","MC","AMEX"].map(b => <span key={b} style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", padding: "2px 7px", fontSize: 9, color: "rgba(255,255,255,.5)", borderRadius: 3 }}>{b}</span>)}</div>
          </div>
          <div className="stripe-toggle">
            <button className={cycle === "annual" ? "on" : ""} onClick={() => setCycle("annual")}>Annual <span className="save-pill">SAVE 31%</span></button>
            <button className={cycle === "monthly" ? "on" : ""} onClick={() => setCycle("monthly")}>Monthly</button>
          </div>
          <div className="stripe-price-big">{price}</div>
          <div className="stripe-price-sub">{cycle === "annual" ? "Billed annually · saves $45" : "Billed monthly"} · Cancel anytime</div>
        </div>
        <div className="stripe-btm">
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--slate-400)", marginBottom: 18 }}>🔒 Secured by Stripe · 256-bit SSL</div>
          {[["Name on Card", name, v => setName(v), "Alex Johnson", 99], ["Card Number", card, v => setCard(fmtCard(v)), "1234 5678 9012 3456", 19]].map(([lbl, val, fn, ph, ml]) => (
            <div key={lbl}><div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--slate-400)", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>{lbl}</div><input className="stripe-inp" placeholder={ph} value={val} onChange={e => fn(e.target.value)} maxLength={ml}/></div>
          ))}
          <div className="card-row">
            <div><div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--slate-400)", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Expiry</div><input className="stripe-inp" placeholder="MM/YY" value={exp} onChange={e => setExp(fmtExp(e.target.value))} maxLength={5}/></div>
            <div><div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--slate-400)", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>CVC</div><input className="stripe-inp" placeholder="123" value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/g,"").slice(0,4))} maxLength={4}/></div>
          </div>
          {error && <div style={{ fontSize: 11, color: "var(--red)", marginBottom: 8 }}>{error}</div>}
          <button className="stripe-submit" onClick={handle} disabled={loading}>{loading ? "Processing…" : `Subscribe — ${price}`}</button>
          <div className="stripe-foot">By subscribing you agree to our Terms. Auto-renews until cancelled.</div>
        </div>
      </div>
    </div>
  );
}

// ─── PAYWALL ──────────────────────────────────────────────────────────────────
function Paywall({ title, subtitle, features, onUpgrade }) {
  return (
    <div className="paywall">
      <div className="paywall-title">{title || "Unlock with Sovereign"}</div>
      <div className="paywall-sub">{subtitle || "Upgrade to access AI-powered insights grounded in your real numbers."}</div>
      <div className="paywall-feats">{(features || ["AI insights engine", "AI scenario builder", "Unlimited AI chat"]).map(f => <div className="paywall-feat" key={f}>{f}</div>)}</div>
      <button className="btn-sage" style={{ fontSize: 14, padding: "13px 32px" }} onClick={onUpgrade}>Upgrade to Sovereign — $12/mo</button>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--slate-400)", marginTop: 10 }}>14-day free trial · Cancel anytime</div>
    </div>
  );
}

// ─── ONBOARDING INTERVIEW ─────────────────────────────────────────────────────
const ONBOARD_SYS = `You are a warm, friendly financial independence coach onboarding a new user to SovereignFI. Have a natural conversation to learn their financial situation.

Collect: income (monthly take-home), expenses (monthly spending), debtPayment (monthly debt payments), portfolio (brokerage/investment accounts), retirement (401k/IRA balance), realEstate (property value), debt (total debt), monthlyGoal (desired monthly spending in retirement), currentAge.

Rules:
- Ask 1-2 questions at a time. Be warm and encouraging.
- Keep messages short (2-4 sentences). This is a chat, not an essay.
- When you have enough info, output EXACTLY this on its own line: EXTRACTED:{"income":N,"expenses":N,"debtPayment":N,"portfolio":N,"retirement":N,"realEstate":N,"debt":N,"monthlyGoal":N,"currentAge":N,"withdrawalRate":4,"returnRate":7}
- Use 0 for anything they skip.
- Start by welcoming them and asking where they are financially today.`;

function OnboardingInterview({ user, onComplete, onSkip }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const bottomRef = useRef(null);
  useEffect(() => { sendAI([]); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const sendAI = async (history) => {
    setLoading(true);
    try {
      const reply = await aiCall(
        history.length ? history : [{ role: "user", content: `Hi, I just signed up. My name is ${user.name || "there"}.` }],
        ONBOARD_SYS
      );
      const m = reply.match(/EXTRACTED:({.+})/);
      if (m) {
        try {
          const data = JSON.parse(m[1]);
          const clean = reply.replace(/EXTRACTED:{.+}/, "").trim();
          if (clean) setMsgs(p => [...p, { role: "assistant", content: clean }]);
          setTimeout(() => onComplete(data), 600);
          return;
        } catch {}
      }
      setMsgs(p => [...p, { role: "assistant", content: reply }]);
      setStep(s => Math.min(s + 1, 8));
    } catch {
      setMsgs(p => [...p, { role: "assistant", content: "Sorry, I had trouble connecting. You can skip and enter your numbers manually." }]);
    }
    setLoading(false);
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    const next = [...msgs, { role: "user", content: msg }];
    setMsgs(next);
    await sendAI(next.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content })));
  };

  return (
    <div className="onboard">
      <div className="onboard-nav">
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "white" }}>Sovereign<em style={{ fontStyle: "italic", color: "var(--sage-400)" }}>FI</em></div>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div className="onboard-pips">{Array.from({ length: 8 }).map((_, i) => <div key={i} className={`onboard-pip${i < step ? " done" : ""}`}/>)}</div>
          <button className="onboard-skip" onClick={onSkip}>Skip — enter manually</button>
        </div>
      </div>
      <div className="onboard-main">
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,.25)", marginBottom: 28, textTransform: "uppercase" }}>AI Financial Interview</div>
        <div className="onboard-thread">
          {msgs.map((m, i) => (
            <div className={`onboard-msg ${m.role === "assistant" ? "ai" : "user"}`} key={i}>
              <div className={`onboard-av ${m.role === "assistant" ? "ai" : "me"}`}>{m.role === "assistant" ? "AI" : "ME"}</div>
              <div className="onboard-bubble">{m.content}</div>
            </div>
          ))}
          {loading && <div className="onboard-msg ai"><div className="onboard-av ai">AI</div><div className="onboard-bubble"><span className="dot"/>&nbsp;<span className="dot"/>&nbsp;<span className="dot"/></div></div>}
          <div ref={bottomRef}/>
        </div>
        <div className="onboard-input-row">
          <input className="onboard-inp" placeholder="Type your answer…" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} autoFocus/>
          <button className="onboard-send" onClick={send} disabled={loading || !input.trim()}>Send</button>
        </div>
      </div>
    </div>
  );
}

// ─── EXPLAIN POPUP ────────────────────────────────────────────────────────────
function ExplainPopup({ label, value, context, position, onClose }) {
  const [text, setText] = useState(""); const [loading, setLoading] = useState(true);
  useEffect(() => {
    aiCall([{ role: "user", content: `Explain "${label}" (${value}) for a FIRE beginner. 2-3 plain English sentences, no jargon. Context: ${context}` }])
      .then(r => { setText(r); setLoading(false); })
      .catch(() => { setText("Could not load explanation."); setLoading(false); });
  }, []);
  return (
    <div className="explain-pop" style={{ top: position.y, left: Math.min(position.x, window.innerWidth - 320) }}>
      <div className="explain-pop-label">{label}</div>
      {loading ? <><span className="dot"/>&nbsp;<span className="dot"/>&nbsp;<span className="dot"/></> : <div>{text}</div>}
      <button className="explain-pop-close" onClick={onClose}>Close</button>
    </div>
  );
}

// ─── AI INSIGHTS ──────────────────────────────────────────────────────────────
function AIInsights({ inputs, fi, isPaid, onUpgrade }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastHash, setLastHash] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const prompt = `Analyze this FIRE profile and return exactly 3 insights as a JSON array. No markdown, no code fences, just raw JSON.
Profile: Income ${fmt(inputs.income)}/mo, Expenses ${fmt(inputs.expenses)}/mo, Investable ${fmt(fi.monthlyInvest)}/mo (${fi.savingsRate}% savings rate), Net worth ${fmt(fi.netWorth)}, FI target ${fmt(fi.fiTarget)} (${fi.pct}% there), FI in ${fi.years} years, Debt ${fmt(inputs.debt)}, Coast FIRE target ${fmt(fi.coastTarget)} (${fi.coastPct}% there, ${fi.coastReached ? "REACHED" : "not reached"}).
Return: [{"type":"OPPORTUNITY|RISK|ACTION","headline":"concise title","text":"2-3 sentences using their actual dollar amounts","monthsImpact":number,"color":"green|amber|red"}]`;
      const raw = await aiCall([{ role: "user", content: prompt }]);
      const parsed = JSON.parse(raw.replace(/```json|```/g, "").trim());
      setInsights(parsed);
      setLastHash(JSON.stringify(inputs));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [inputs, fi]);

  useEffect(() => { if (isPaid) run(); }, [isPaid]);
  const stale = lastHash && lastHash !== JSON.stringify(inputs);
  const colors = { green: "var(--sage-600)", amber: "var(--amber)", red: "var(--red)" };

  if (!isPaid) return <Paywall title="AI-Powered Insights" subtitle="Your numbers tell a story. Let AI find the opportunities and risks you're missing." features={["Personalized opportunity analysis", "Risk detection with dollar amounts", "FI date impact estimates", "Coast FIRE milestone alerts"]} onUpgrade={onUpgrade}/>;

  return (
    <div className="insights-section">
      <div className="section-header">
        <div className="section-title"><span className="ai-pill">AI</span> Personalized Insights {stale && !loading && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--amber)", fontWeight: 400 }}>· numbers changed</span>}</div>
        <button className="section-action" onClick={run} disabled={loading}>{loading ? <><span className="dot"/><span className="dot"/><span className="dot"/></> : stale ? "Refresh" : "Re-analyze"}</button>
      </div>
      {loading && !insights && <div className="ai-loading-row"><span className="dot"/><span className="dot"/><span className="dot"/><span style={{ marginLeft: 8 }}>Analyzing your profile…</span></div>}
      {error && <div className="ai-err">Failed to load insights: {error}</div>}
      {insights && !loading && (
        <div className="insights-grid">
          {insights.map((ins, i) => {
            const c = colors[ins.color] || "var(--sage-600)";
            return (
              <div className="insight-item" key={i}>
                <div className="insight-type" style={{ color: c }}>{ins.type}</div>
                <div className="insight-headline">{ins.headline}</div>
                <div className="insight-text">{ins.text}</div>
                <div className="insight-impact">FI date impact
                  <div className="insight-impact-num" style={{ color: ins.monthsImpact > 0 ? "var(--sage-600)" : "var(--red)" }}>
                    {ins.monthsImpact > 0 ? "▲" : "▼"} {Math.abs(ins.monthsImpact)} months {ins.monthsImpact > 0 ? "sooner" : "later"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {insights && <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--slate-400)", marginTop: 14 }}>Generated {new Date().toLocaleTimeString()}{stale ? " · refresh to update" : ""}</div>}
    </div>
  );
}

// ─── AI SCENARIO BUILDER ──────────────────────────────────────────────────────
function AIScenarios({ inputs, fi, isPaid, onUpgrade }) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const chips = ["What if I moved abroad?", "What if I got a $20k raise?", "What if I paid off all debt?", "What if markets crashed 40%?", "What if I went part-time?", "What if I inherited $100k?"];

  const run = async (q) => {
    const question = q || query;
    if (!question.trim()) return;
    setLoading(true); setResult(null);
    try {
      const prompt = `Financial profile: Income ${fmt(inputs.income)}/mo, Expenses ${fmt(inputs.expenses)}/mo, Debt payments ${fmt(inputs.debtPayment)}/mo, Portfolio ${fmt(inputs.portfolio)}, Retirement ${fmt(inputs.retirement)}, Real estate ${fmt(inputs.realEstate)}, Debt ${fmt(inputs.debt)}, Retirement goal ${fmt(inputs.monthlyGoal)}/mo, FI target ${fmt(fi.fiTarget)}, FI in ${fi.years} years (${fi.fiMonth} ${fi.fiYear}).
Question: "${question}"
Return raw JSON only, no markdown: {"summary":"2-3 sentence plain English answer with specific numbers","newIncome":N,"newExpenses":N,"newDebtPayment":N,"newPortfolio":N,"newRetirement":N,"newRealEstate":N,"newDebt":N,"assumption":"1 sentence about your key assumption"}
Use original values for numbers not affected by this scenario.`;
      const raw = await aiCall([{ role: "user", content: prompt }]);
      const data = JSON.parse(raw.replace(/```json|```/g, "").trim());
      const ni = { ...inputs, income: data.newIncome || inputs.income, expenses: data.newExpenses || inputs.expenses, debtPayment: data.newDebtPayment || inputs.debtPayment, portfolio: data.newPortfolio || inputs.portfolio, retirement: data.newRetirement || inputs.retirement, realEstate: data.newRealEstate || inputs.realEstate, debt: data.newDebt || inputs.debt };
      const nfi = calcFI(ni);
      setResult({ ...data, newYears: nfi.years, newDate: nfi.date, delta: nfi.years - fi.years, question });
    } catch (e) { setResult({ error: e.message }); }
    setLoading(false);
  };

  if (!isPaid) return <Paywall title="AI Scenario Builder" subtitle='Ask anything. "What if I moved abroad?" "What if I got a raise?" See the exact impact on your FI date.' features={["Natural language scenario modeling", "Exact FI date change shown", "Considers all financial variables", "Unlimited scenarios"]} onUpgrade={onUpgrade}/>;

  return (
    <div className="scenario-section">
      <div className="section-header">
        <div className="section-title"><span className="ai-pill">AI</span> Scenario Builder</div>
      </div>
      <div className="scenario-chips">{chips.map(c => <button key={c} className="s-chip" onClick={() => { setQuery(c); run(c); }}>{c}</button>)}</div>
      <div className="scenario-input-row">
        <input className="scenario-inp" placeholder='Ask any "what if" — "What if I freelanced?" or "What if I had a kid?"' value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && run()}/>
        <button className="scenario-run" onClick={() => run()} disabled={loading || !query.trim()}>{loading ? "Modeling…" : "Run Scenario"}</button>
      </div>
      {loading && <div className="ai-loading-row"><span className="dot"/><span className="dot"/><span className="dot"/><span style={{ marginLeft: 8 }}>Modeling your scenario…</span></div>}
      {result && !loading && (
        result.error ? <div className="ai-err">Error: {result.error}</div> :
        <div className="scenario-result">
          <div className="scenario-q">"{result.question}"</div>
          <div className="scenario-summary">{result.summary}</div>
          <div className="scenario-assumption">Assumption: {result.assumption}</div>
          <div className="scenario-delta" style={{ color: result.delta <= 0 ? "var(--sage-600)" : "var(--red)" }}>
            {result.delta === 0 ? "No change to your FI date" : result.delta < 0 ? `${Math.abs(result.delta)} years sooner — ${result.newDate.toLocaleString("default",{month:"long"})} ${result.newDate.getFullYear()}` : `${result.delta} years later — ${result.newDate.toLocaleString("default",{month:"long"})} ${result.newDate.getFullYear()}`}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI CHAT ──────────────────────────────────────────────────────────────────
function AIChat({ inputs, fi, isPaid, onUpgrade }) {
  const initMsg = `I can see your full picture. You're ${fi.years} years from financial independence with a ${fi.savingsRate}% savings rate — you've covered ${fi.pct}% of your ${fmt(fi.fiTarget)} FI target. What do you want to work through?`;
  const [msgs, setMsgs] = useState([{ role: "assistant", content: initMsg }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    const newMsgs = [...msgs, { role: "user", content: msg }];
    setMsgs(newMsgs);
    setLoading(true);
    try {
      const sys = `You are SovereignFI's personal AI advisor. You know this user's complete financial picture: Monthly income ${fmt(inputs.income)}, expenses ${fmt(inputs.expenses)}, debt payments ${fmt(inputs.debtPayment)}, investable ${fmt(fi.monthlyInvest)}/mo (${fi.savingsRate}% savings rate), net worth ${fmt(fi.netWorth)}, FI target ${fmt(fi.fiTarget)} (${fi.pct}% complete), ${fi.years} years to FI, total debt ${fmt(inputs.debt)}, Coast FIRE target ${fmt(fi.coastTarget)} (${fi.coastReached ? "already reached!" : fi.coastPct + "% there"}).

Be specific and always reference their actual numbers. Give concrete, actionable advice. Keep answers to 3-5 sentences. You are not a generic AI — you know their exact situation.`;
      const history = newMsgs.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }));
      const reply = await aiCall(history, sys);
      setMsgs(p => [...p, { role: "assistant", content: reply }]);
    } catch (e) {
      setMsgs(p => [...p, { role: "assistant", content: "Sorry, I had trouble connecting. Please try again." }]);
    }
    setLoading(false);
  };

  if (!isPaid) return <Paywall title="Your Personal FI Advisor" subtitle="Unlike generic AI chats, your advisor already knows your numbers. No re-explaining. No starting over. Just answers." features={["Knows your complete financial picture", "No re-hashing your situation", "Ask anything, any time", "Grounded in your real numbers"]} onUpgrade={onUpgrade}/>;

  const chips = ["How do I reach FI 5 years sooner?", "Should I pay debt or invest?", "Am I on track?", "Explain Coast FIRE"];
  return (
    <div className="chat-section">
      <div className="section-header">
        <div className="section-title"><span className="ai-pill">AI</span> Your FI Advisor</div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--slate-400)" }}>Knows your numbers · no re-explaining needed</div>
      </div>
      {msgs.length === 1 && <div className="chat-chips">{chips.map(c => <button key={c} className="chat-chip" onClick={() => send(c)}>{c}</button>)}</div>}
      <div className="chat-thread">
        {msgs.map((m, i) => (
          <div className={`chat-msg ${m.role === "assistant" ? "ai" : "user"}`} key={i}>
            <div className={`chat-av ${m.role === "assistant" ? "ai" : "me"}`}>{m.role === "assistant" ? "AI" : "ME"}</div>
            <div className="chat-bubble">{m.content}</div>
          </div>
        ))}
        {loading && <div className="chat-msg ai"><div className="chat-av ai">AI</div><div className="chat-bubble"><span className="dot"/>&nbsp;<span className="dot"/>&nbsp;<span className="dot"/></div></div>}
        <div ref={bottomRef}/>
      </div>
      <div className="chat-input-row">
        <input className="chat-inp" placeholder="Ask anything about your finances…" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}/>
        <button className="chat-send" onClick={() => send()} disabled={loading || !input.trim()}>Send</button>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ user, onSignOut, onUpgrade, onBack }) {
  const [inputs, setInputs] = useState(DEF);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [explainPopup, setExplainPopup] = useState(null);
  const isPaid = user?.plan === "sovereign";

  useEffect(() => {
    const saved = store.get(`profile:${user.email}`);
    if (saved) setInputs(saved);
    setLoaded(true);
  }, [user.email]);

  const upd = (k, v) => {
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
    if (!isNaN(n)) { const next = { ...inputs, [k]: n }; setInputs(next); store.set(`profile:${user.email}`, next); }
  };

  const fi = calcFI(inputs);
  const bars = (() => {
    const r = inputs.returnRate / 100;
    const monthly = Math.max(0, fi.monthlyInvest);
    // Split contributions proportionally between retirement and brokerage
    const totalInvest = inputs.portfolio + inputs.retirement;
    const retirePct = totalInvest > 0 ? inputs.retirement / totalInvest : 0.5;
    const brokeragePct = 1 - retirePct;
    const data = Array.from({ length: 21 }, (_, i) => {
      const growthFactor = Math.pow(1 + r, i);
      const contribFactor = r > 0 ? (growthFactor - 1) / r : i;
      const retireVal = inputs.retirement * growthFactor + monthly * retirePct * 12 * contribFactor;
      const brokerageVal = inputs.portfolio * growthFactor + monthly * brokeragePct * 12 * contribFactor;
      return { retire: Math.max(0, retireVal), brokerage: Math.max(0, brokerageVal), total: Math.max(0, retireVal + brokerageVal) };
    });
    const maxVal = Math.max(...data.map(d => d.total), fi.fiTarget, 1);
    return data.slice(0, 20).map((d, i) => ({
      retirePct: Math.min(98, d.retire / maxVal * 100),
      brokeragePct: Math.min(98, d.brokerage / maxVal * 100),
      totalPct: Math.min(98, Math.max(2, d.total / maxVal * 100)),
      dollars: d.total,
      fiLinePct: Math.min(97, fi.fiTarget / maxVal * 100),
      isFiYear: i > 0 && d.total >= fi.fiTarget && data[i-1].total < fi.fiTarget,
      maxVal,
    }));
  })();

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: "◎" },
    { id: "inputs", label: "Your Numbers", icon: "⊟" },
    { id: "insights", label: "AI Insights", icon: "✦" },
    { id: "scenarios", label: "Scenarios", icon: "◈" },
    { id: "advisor", label: "AI Advisor", icon: "⊕" },
  ];

  const stats = [
    { label: "Monthly Investable", val: fmt(fi.monthlyInvest), sub: `${fi.savingsRate}% savings rate`, cls: fi.monthlyInvest > 0 ? "pos" : "neg", context: `This is what you invest each month after all expenses and debt payments.` },
    { label: "Net Worth", val: fmt(fi.netWorth), sub: "Assets minus debt", cls: "", context: `Total value of all your assets minus all your debts.` },
    { label: "FI Target", val: fmtShort(fi.fiTarget), sub: `${inputs.withdrawalRate}% at retirement`, cls: "", context: `The nest egg you need to retire safely at a ${inputs.withdrawalRate}% annual withdrawal rate.` },
    { label: "Savings Rate", val: `${fi.savingsRate}%`, sub: fi.savingsRate > 20 ? "Strong" : "Room to grow", cls: fi.savingsRate > 20 ? "pos" : "", context: `The percentage of your income you're saving and investing each month.` },
  ];

  return (
    <div className="shell">
      {explainPopup && <ExplainPopup {...explainPopup} onClose={() => setExplainPopup(null)}/>}
      <div className="sidebar">
        <div className="s-brand" onClick={onBack}>Sovereign<em>FI</em></div>
        <div className="s-user">
          <div className="s-av">{(user.name || user.email)[0].toUpperCase()}</div>
          <div><div className="s-name">{user.name || user.email.split("@")[0]}</div><div className="s-plan">{isPaid ? "SOVEREIGN" : "FREE PLAN"}</div></div>
        </div>
        <div className="s-nav">
          {navItems.map(n => (
            <div key={n.id} className={`s-item${activeTab === n.id ? " active" : ""}`} onClick={() => setActiveTab(n.id)}>
              <span className="s-icon">{n.icon}</span>{n.label}
            </div>
          ))}
        </div>
        {!isPaid && (
          <div style={{ margin: "12px 0" }}>
            <button className="btn-sage" style={{ width: "100%", fontSize: 12, padding: "9px" }} onClick={onUpgrade}>Upgrade to Sovereign</button>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "rgba(255,255,255,.25)", textAlign: "center", marginTop: 6 }}>AI insights + advisor</div>
          </div>
        )}
        <div className="s-sep"/>
        <div className="s-item" style={{ color: "rgba(255,255,255,.22)" }} onClick={onSignOut}>Sign Out</div>
        <div className="s-fi-widget">
          <div className="s-fi-lbl">YOUR FI DATE</div>
          <div className="s-fi-date">{fi.fiMonth} {fi.fiYear}</div>
          <div className="s-fi-sub">{fi.years} years away</div>
        </div>
      </div>

      <div className="main">
        <div className="main-inner">

          {/* ── DASHBOARD TAB ── */}
          {activeTab === "dashboard" && <>
            <div className="pg-header">
              <div>
                <div className="pg-title">Good to see you, {user.name?.split(" ")[0] || "there"}.</div>
                <div className="pg-sub">Your financial independence is tracked in real time — no new chats, no repeating yourself.</div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {!isPaid && <button className="btn-sage" style={{ fontSize: 12 }} onClick={onUpgrade}>Unlock AI Features</button>}
                <div className="live-badge"><div className="live-dot"/>Live</div>
              </div>
            </div>

            {!isPaid && (
              <div className="upgrade-bar">
                <div>
                  <div className="upgrade-bar-text">Upgrade to unlock your AI advisor and personalized insights</div>
                  <div className="upgrade-bar-sub">Unlike ChatGPT, your advisor already knows your numbers — no re-explaining ever</div>
                </div>
                <button className="btn-sage" style={{ fontSize: 12, whiteSpace: "nowrap" }} onClick={onUpgrade}>Upgrade — $12/mo</button>
              </div>
            )}

            <div className="fi-hero">
              <div className="fi-date-col">
                <div className="fi-date-label">Financial Independence Date</div>
                <div className="fi-date-big"><em>{fi.fiMonth}</em> {fi.fiYear}</div>
                <div className="fi-date-sub">{fi.years} years from today</div>
              </div>
              <div className="fi-prog-col">
                <div className="fi-prog-label-row"><span>Progress to FI target</span><span>{fi.pct}%</span></div>
                <div className="fi-prog-track"><div className="fi-prog-fill" style={{ width: `${fi.pct}%` }}/></div>
                <div className="fi-prog-amounts"><span>{fmt(fi.netWorth)}</span><span>{fmt(fi.fiTarget)}</span></div>
              </div>
              <div className="fi-right-col">
                <div className="fi-right-num">+14mo</div>
                <div className="fi-right-lbl">ahead of last year</div>
              </div>
            </div>

            <div className="stats-row">
              {stats.map(({ label, val, sub, cls, context }) => (
                <div className="stat-item" key={label}>
                  <div className="stat-lbl">{label}</div>
                  <div className={`stat-val ${cls}`}>{val}</div>
                  <div className="stat-sub">{sub}</div>
                  <button className="stat-explain" onClick={e => {
                    const rect = e.target.getBoundingClientRect();
                    setExplainPopup({ label, value: val, context, position: { x: rect.left, y: rect.bottom + 8 } });
                  }}>? explain</button>
                </div>
              ))}
            </div>

            <div className="coast-section">
              <div>
                <div className="coast-lbl">Coast FIRE</div>
                <div className="coast-val-big">{fmtShort(fi.coastTarget)}</div>
                <div className="coast-desc">
                  If your investments reach <strong>{fmtShort(fi.coastTarget)}</strong> today, you can stop contributing entirely and — assuming {inputs.returnRate}% annual growth — your portfolio will grow on its own to {fmtShort(fi.fiTarget)} by age {inputs.retirementAge || 65}. You'd never need to invest another dollar.
                </div>
                <div style={{ marginTop: 10, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: "var(--slate-400)", lineHeight: 1.6 }}>
                  Based on: {inputs.returnRate}% return · retire at {inputs.retirementAge || 65} · {inputs.withdrawalRate}% withdrawal · {fmt(inputs.monthlyGoal)}/mo goal
                </div>
              </div>
              <div>
                <div className="coast-prog-lbl"><span>Coast progress</span><span>{fi.coastPct}%</span></div>
                <div className="coast-track"><div className="coast-fill" style={{ width: `${fi.coastPct}%` }}/></div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--slate-400)", marginTop: 8 }}>{fmt(fi.netWorth)} of {fmt(fi.coastTarget)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                {fi.coastReached
                  ? <div className="coast-reached">✓ Coast FIRE Reached</div>
                  : <><div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, color: "var(--coast)", letterSpacing: -1 }}>{fmtShort(fi.coastTarget - fi.netWorth)}</div><div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--slate-400)", marginTop: 4 }}>still needed</div></>}
              </div>
            </div>

            <div className="chart-section">
              <div className="section-header">
                <div className="section-title">Investment Portfolio Growth — next 20 years</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--slate-400)" }}>Based on {inputs.returnRate}% return · {fmt(Math.max(0,fi.monthlyInvest))}/mo contributions</div>
              </div>
              {bars.length > 0 && <div style={{ display: "flex", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", paddingBottom: 28, paddingTop: 4, minWidth: 48 }}>
                  {[1, 0.75, 0.5, 0.25, 0].map((pct, i) => (
                    <div key={i} style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "var(--slate-500)", textAlign: "right", whiteSpace: "nowrap", fontWeight: 500 }}>{fmtShort(bars[0].maxVal * pct)}</div>
                  ))}
                </div>
                <div style={{ flex: 1, position: "relative" }}>
                  <div style={{ position: "absolute", left: 0, right: 0, bottom: `${bars[0].fiLinePct}%`, zIndex: 1 }}>
                    <div style={{ borderTop: "2px solid var(--sage-600)", opacity: .85, position: "relative" }}>
                      <span style={{ position: "absolute", right: 0, top: -18, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "var(--sage-600)", fontWeight: 500, background: "var(--white)", paddingLeft: 6 }}>— FI target: {fmtShort(fi.fiTarget)}</span>
                    </div>
                  </div>
                  <div style={{ height: 160, display: "flex", alignItems: "flex-end", gap: 2, position: "relative", zIndex: 2 }}>
                    {bars.map((b, i) => (
                      <div key={i} style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", position: "relative" }}>
                        {b.isFiYear && <div style={{ position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)", fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: "var(--sage-600)", whiteSpace: "nowrap" }}>FI ✓</div>}
                        <div style={{ width: "100%", display: "flex", flexDirection: "column", height: `${b.totalPct}%`, minHeight: 2, borderRadius: "1px 1px 0 0", overflow: "hidden" }}>
                          <div style={{ flex: b.retirePct, background: "var(--coast)", opacity: .65 }}/>
                          <div style={{ flex: b.brokeragePct, background: "var(--sage-500)", opacity: .7 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                    {[0,5,10,15,20].map(y => (
                      <div key={y} style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "var(--slate-500)", fontWeight: 500 }}>Yr {y}</div>
                    ))}
                  </div>
                </div>
              </div>}
              <div className="chart-leg" style={{ marginTop: 14 }}>
                <div className="chart-leg-item"><div className="chart-swatch" style={{ background: "var(--coast)", opacity: .65 }}/> Retirement accounts (401k / IRA)</div>
                <div className="chart-leg-item"><div className="chart-swatch" style={{ background: "var(--sage-500)", opacity: .7 }}/> Brokerage / investment accounts</div>
                <div className="chart-leg-item"><div style={{ width: 16, height: 1, borderTop: "1px dashed var(--sage-500)" }}/> FI target ({fmtShort(fi.fiTarget)})</div>
              </div>
            </div>
          </>}

          {/* ── INPUTS TAB ── */}
          {activeTab === "inputs" && <>
            <div className="pg-header">
              <div>
                <div className="pg-title">Your Numbers</div>
                <div className="pg-sub">Edit any field and press Enter — your FI date updates instantly.</div>
              </div>
            </div>
            <div className="inputs-section">
              <div className="inputs-grid">
                {[
                  { title: "Income & Spending", fields: [["Monthly Take-Home", "income"], ["Monthly Expenses", "expenses"], ["Debt Payments /mo", "debtPayment"], ["Monthly Retirement Goal", "monthlyGoal"]] },
                  { title: "Assets", fields: [["Investment Portfolio", "portfolio"], ["Retirement Accounts", "retirement"], ["Real Estate Value", "realEstate"]] },
                  { title: "Debt", fields: [["Total Debt Owed", "debt"]] },
                  { title: "Assumptions", fields: [["Current Age", "currentAge"], ["Withdrawal Rate % at Retirement", "withdrawalRate"], ["Expected Return %", "returnRate"]] },
                ].map(({ title, fields }) => (
                  <div className="input-group" key={title}>
                    <div className="input-group-title">{title}</div>
                    {fields.map(([lbl, k]) => (
                      <div className="inp-row" key={k}>
                        <span className="inp-lbl">{lbl}</span>
                        <input key={`${k}-${loaded}`} className="inp-field" defaultValue={["currentAge","withdrawalRate","returnRate"].includes(k) ? inputs[k] : fmt(inputs[k])} onBlur={e => upd(k, e.target.value)} onKeyDown={e => e.key === "Enter" && upd(k, e.target.value)}/>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </>}

          {/* ── INSIGHTS TAB ── */}
          {activeTab === "insights" && <>
            <div className="pg-header">
              <div>
                <div className="pg-title">AI Insights</div>
                <div className="pg-sub">Personalized analysis of your financial profile — opportunities, risks, and actions.</div>
              </div>
            </div>
            <AIInsights inputs={inputs} fi={fi} isPaid={isPaid} onUpgrade={onUpgrade}/>
          </>}

          {/* ── SCENARIOS TAB ── */}
          {activeTab === "scenarios" && <>
            <div className="pg-header">
              <div>
                <div className="pg-title">Scenario Builder</div>
                <div className="pg-sub">Ask any what-if and see the exact impact on your FI date.</div>
              </div>
            </div>
            <AIScenarios inputs={inputs} fi={fi} isPaid={isPaid} onUpgrade={onUpgrade}/>
          </>}

          {/* ── ADVISOR TAB ── */}
          {activeTab === "advisor" && <>
            <div className="pg-header">
              <div>
                <div className="pg-title">Your FI Advisor</div>
                <div className="pg-sub">Already knows your complete financial picture. Ask anything — no re-explaining needed.</div>
              </div>
            </div>
            <AIChat inputs={inputs} fi={fi} isPaid={isPaid} onUpgrade={onUpgrade}/>
          </>}

        </div>
      </div>
    </div>
  );
}

// ─── LANDING ──────────────────────────────────────────────────────────────────
function Landing({ onSignIn, onSignUp }) {
  return (
    <div>
      <nav className="nav">
        <div className="logo">Sovereign<em>FI</em></div>
        <div className="nav-links">
          <span className="nav-link" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({behavior:'smooth'})}>How it works</span>
          <span className="nav-link" onClick={() => document.getElementById('pricing')?.scrollIntoView({behavior:'smooth'})}>Pricing</span>
          <span className="nav-link" onClick={() => document.getElementById('about')?.scrollIntoView({behavior:'smooth'})}>About</span>
        </div>
        <div className="nav-right">
          <button className="btn-ghost" onClick={onSignIn}>Sign in</button>
          <button className="btn-primary" onClick={onSignUp}>Start free</button>
        </div>
      </nav>

      <div className="hero">
        <div className="hero-l">
          <div className="hero-kicker">Real-time FI tracking</div>
          <h1 className="hero-h1">Your financial independence date, <em>always current.</em></h1>
          <p className="hero-body">
            Stop starting the same conversation over with generic AI. SovereignFI <strong>already knows your numbers</strong> — your income, your debt, your goals. It tracks your independence date in real time and gives you an AI advisor that never forgets.
          </p>
          <div className="hero-btns">
            <button className="btn-hero primary" onClick={onSignUp}>Start free — no card needed</button>
            <button className="btn-hero ghost" onClick={onSignIn}>Sign in</button>
          </div>
          <div className="hero-proof">
            <div className="proof-stat"><div className="proof-num">4%</div><div className="proof-lbl">withdrawal rule built in</div></div>
            <div className="proof-stat"><div className="proof-num">Live</div><div className="proof-lbl">FI date recalculation</div></div>
            <div className="proof-stat"><div className="proof-num">AI</div><div className="proof-lbl">that knows your numbers</div></div>
          </div>
        </div>
        <div className="hero-r">
          <div className="mock-wrap">
            <div className="mock-label">Your financial independence date</div>
            <div className="mock-date-big">March 2039</div>
            <div className="mock-date-sub">↑ 14 months ahead of last year</div>
            <div className="mock-prog-label"><span>FI progress</span><span>68%</span></div>
            <div className="mock-prog-track"><div className="mock-prog-fill" style={{ width: "68%" }}/></div>
            <div className="mock-stats">
              {[["$847k","Net Worth"],["$1.24M","FI Target"],["$612k","Coast FIRE"],["14.2yr","Remaining"]].map(([v,l]) => (
                <div className="mock-stat" key={l}><div className="mock-stat-v">{v}</div><div className="mock-stat-l">{l}</div></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="why-section" id="how-it-works">
        <div className="section-kicker">Why SovereignFI</div>
        <h2 className="section-h2">The problem with using ChatGPT for FIRE planning</h2>
        <div className="why-grid">
          {[
            ["01", "You re-explain yourself every time", "Every new conversation starts from zero. Your income, expenses, goals, net worth — you paste it all in again. That's not a tool, that's a chore."],
            ["02", "Generic AI gives generic advice", "ChatGPT doesn't know your actual savings rate or debt balance. SovereignFI does. The difference between 'pay off debt' and 'pay off your $210k at 6.8% while investing $3,200/mo' is everything."],
            ["03", "Your FI date isn't a static number", "Markets move. Your income changes. Your expenses shift. SovereignFI recalculates in real time so you always know exactly where you stand — not where you were six months ago."],
            ["04", "Coast FIRE is invisible elsewhere", "Most FIRE tools skip Coast FIRE entirely. We show you exactly when you can stop investing and still retire at 65 — often years sooner than you think."],
            ["05", "Scenarios require a spreadsheet", "Wondering what a $15k raise does to your FI date? Or moving to Portugal? SovereignFI models it in seconds, with AI that understands your actual numbers."],
            ["06", "One place for everything", "Your FI date, your net worth trajectory, your AI advisor, your scenario modeling — all in one dashboard that updates the moment you change a number."],
          ].map(([num, title, desc]) => (
            <div className="why-item" key={num}>
              <div className="why-num">{num}</div>
              <div className="why-title">{title}</div>
              <div className="why-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="features-section" id="features">
        <div className="section-kicker">Features</div>
        <h2 className="section-h2">Everything you need to track your independence</h2>
        <div className="features-grid">
          {[
            ["AI ONBOARDING", "AI Financial Interview", "Just tell our AI where you are financially. It extracts your numbers through a natural conversation and sets up your dashboard — no forms, no spreadsheets."],
            ["REAL-TIME", "Live FI Date Modeling", "Change any number and your independence date recalculates instantly. Watch your FI date move as you optimize your finances."],
            ["COAST FIRE", "Coast FIRE Tracker", "See exactly when you can stop investing entirely and still retire at 65. Most people hit it earlier than they expect."],
            ["AI POWERED", "Personalized Insights", "AI analyzes your specific profile and surfaces the opportunities you're missing — with dollar amounts and FI date impact."],
            ["SCENARIO AI", "Natural Language Scenarios", 'Ask "what if I moved abroad?" or "what if I got a raise?" and get an instant, accurate FI date impact based on your real numbers.'],
            ["YOUR ADVISOR", "AI Advisor That Knows You", "Your personal FI advisor that never forgets your situation. Ask anything, any time, without starting over."],
          ].map(([tag, title, desc]) => (
            <div className="feature-item" key={title}>
              <div className="feature-tag">{tag}</div>
              <div className="feature-title">{title}</div>
              <div className="feature-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-section" id="pricing">
        <div style={{ textAlign: "center", marginBottom: 0 }}>
          <div className="section-kicker" style={{ justifyContent: "center" }}>Pricing</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(34px,3vw,48px)", fontWeight: 500, letterSpacing: "-.3px", color: "var(--slate-950)", marginBottom: 0 }}>Simple, honest pricing</h2>
        </div>
        <div className="pricing-grid">
          {[
            { tier: "Explorer", price: "Free", period: "forever", badge: null, items: ["Live FI date calculator", "Coast FIRE tracker", "Net worth modeling", "Trajectory chart"], featured: false },
            { tier: "Sovereign", price: "$12", period: "per month", badge: "Most Popular", items: ["Everything in Explorer", "AI financial interview", "AI personalized insights", "AI scenario builder", "AI advisor — knows your numbers", "No re-explaining, ever"], featured: true },
            { tier: "Advisor", price: "$29", id: "for-advisors", period: "per month", badge: null, items: ["Everything in Sovereign", "Spouse / partner modeling", "Client sharing & reports", "White-label dashboard"], featured: false },
          ].map(p => (
            <div className={`price-col${p.featured ? " featured" : ""}`} key={p.tier} id={p.id || undefined}>
              {p.badge && <div className="price-badge">{p.badge}</div>}
              <div className="price-tier">{p.tier}</div>
              <div className="price-amount">{p.price}</div>
              <div className="price-period">{p.period}</div>
              <ul className="price-list">{p.items.map(i => <li key={i}>{i}</li>)}</ul>
              <button className={`price-btn${p.featured ? " featured" : ""}`} onClick={onSignUp}>{p.featured ? "Start Free Trial" : "Get Started"}</button>
            </div>
          ))}
        </div>
      </div>

      <div className="about-section" id="about">
        <div className="section-kicker" style={{ color: "var(--sage-400)" }}>About SovereignFI</div>
        <div className="about-grid">
          <div>
            <h2 className="about-h2">Built for people who want to know exactly when they'll be <em>free.</em></h2>
            <p className="about-body">SovereignFI started from a simple frustration: <strong>there was no good place to track financial independence in real time.</strong> Spreadsheets go stale. Generic AI forgets your situation the moment you close the tab. FIRE calculators give you a number but no context.</p>
            <p className="about-body">We built SovereignFI to be the living dashboard your FI journey deserves — one that knows your numbers, remembers your situation, and gives you an AI advisor that actually understands where you stand today.</p>
            <p className="about-body">No spreadsheets. No re-explaining yourself. Just your independence date, always current.</p>
          </div>
          <div className="about-stats">
            {[["4%","Safe withdrawal rate built in"],["7%","Default market return assumption"],["2","FIRE milestones tracked (FI + Coast)"],["0","Times you re-explain your situation"]].map(([num, lbl]) => (
              <div className="about-stat" key={lbl}>
                <div className="about-stat-num">{num}</div>
                <div className="about-stat-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [view, setView] = useState("landing");
  const [user, setUser] = useState(null);
  const [authModal, setAuthModal] = useState(null);
  const [showStripe, setShowStripe] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const handleAuth = u => {
    setUser(u); setAuthModal(null);
    const hasProfile = store.get(`profile:${u.email}`);
    setView(hasProfile ? "app" : "onboard");
    showToast(`Welcome${u.name ? ", " + u.name.split(" ")[0] : ""}!`);
  };

  const handleOnboardComplete = data => {
    store.set(`profile:${user.email}`, { ...DEF, ...data });
    setView("app");
    showToast("Dashboard ready — your numbers are loaded.");
  };

  const handleUpgrade = u => {
    setUser(u); setShowStripe(false);
    showToast("Welcome to Sovereign. All AI features unlocked.");
  };

  const handleSignOut = () => { setUser(null); setView("landing"); showToast("Signed out."); };

  return (
    <>
      <style>{css}</style>
      {view === "landing" && <Landing onSignIn={() => setAuthModal("signin")} onSignUp={() => setAuthModal("signup")}/>}
      {view === "onboard" && user && <OnboardingInterview user={user} onComplete={handleOnboardComplete} onSkip={() => setView("app")}/>}
      {view === "app" && user && <Dashboard user={user} onSignOut={handleSignOut} onUpgrade={() => setShowStripe(true)} onBack={() => setView("landing")}/>}
      {authModal && <AuthModal mode={authModal} onClose={() => setAuthModal(null)} onSuccess={handleAuth}/>}
      {showStripe && user && <StripeModal user={user} onClose={() => setShowStripe(false)} onSuccess={handleUpgrade}/>}
      {toast && <div className="toast"><div className="toast-check">✓</div>{toast}</div>}
    </>
  );
}
